import * as yup from "yup";
import { validateCPF } from "validations-br";

import i18n from "@/translate/i18n";
import config from "@/config";

export const brDateRegex =
  /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/i;

export const userDataSchema = yup.object().shape({
  document_number: yup
    .string()
    .required(i18n.t("form.cpfRequired"))
    .test("validate-cpf", i18n.t("form.invalidCpf"), (value) =>
      validateCPF(value),
    ),

  email: yup
    .string()
    .email(i18n.t("form.invalidEmail"))
    .required(i18n.t("form.emailRequired")),

  merchant_request_amount: yup
    .number()
    .min(1, i18n.t("form.minAmount", { value: 1, currency: "BRL" }))
    .required(i18n.t("form.amountRequired", { value: 3 })),

  accepted_terms: config.requireTerms
    ? yup.bool().oneOf([true], i18n.t("form.acceptedTermsRequired")).required()
    : yup.bool(),
});

export const conditionalUserSchema = yup.object().shape({
  document_number: yup
    .string()
    .required(i18n.t("form.documentRequired"))
    .when("country_iso", (country_iso, schema) => {
      if (country_iso === "BR") {
        return schema.test(
          "validate-cpf",
          i18n.t("form.invalidDocument"),
          (value) => validateCPF(value),
        );
      }

      if (country_iso === "AR") {
        return schema.test(
          "validate-dni",
          i18n.t("form.invalidDocument"),
          (value) => {
            const cleanValue = String(value).replace(/[^\d]/g, "");
            return /^[0-9]+$/.test(cleanValue) && cleanValue.length === 11;
          },
        );
      }

      return schema;
    }),

  document_type: yup.string().when([], {
    is: (_value, schemaContext) =>
      schemaContext?.options?.context?.missingDocuments,
    then: (schema) => schema.required(i18n.t("form.documentTypeRequired")),
    otherwise: (schema) => schema,
  }),

  country_iso: yup.string().when([], {
    is: (_value, schemaContext) =>
      schemaContext?.options?.context?.missingDocuments,
    then: (schema) => schema.required(i18n.t("form.countryRequired")),
    otherwise: (schema) => schema,
  }),

  email: yup
    .string()
    .email(i18n.t("form.invalidEmail"))
    .when([], {
      is: (_value, schemaContext) =>
        !schemaContext?.options?.context?.missingDocuments,
      then: (schema) => schema.required(i18n.t("form.emailRequired")),
      otherwise: (schema) => schema,
    }),

  merchant_request_amount: yup
    .number()
    .min(1, i18n.t("form.minAmount", { value: 1, currency: "BRL" }))
    .when([], {
      is: (_value, schemaContext) =>
        !schemaContext?.options?.context?.missingDocuments,
      then: (schema) =>
        schema.required(i18n.t("form.amountRequired", { value: 3 })),
      otherwise: (schema) => schema,
    }),

  accepted_terms: config.requireTerms
    ? yup
        .bool()
        .oneOf([true], i18n.t("form.acceptedTermsRequired"))
        .when([], {
          is: (_value, schemaContext) =>
            !schemaContext?.options?.context?.missingDocuments,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema,
        })
    : yup.bool(),
});

export const addressSchema = yup.object().shape({
  address: yup.string().required(i18n.t("addressForm.addressRequired")),
  postal_code: yup.string().required(i18n.t("addressForm.postalCodeRequired")),
  state: yup.string().required(i18n.t("addressForm.stateRequired")),
  city: yup
    .string()
    .min(3, i18n.t("addressForm.invalidCity"))
    .required(i18n.t("addressForm.cityRequired")),
});

export const pixKeySchema = yup.object().shape({
  accepted_terms: config.requireTerms
    ? yup.bool().oneOf([true], i18n.t("form.acceptedTermsRequired"))
    : yup.bool(),
});

export const validateSchema = async (data, schema) =>
  schema.isValid(data).then((validation) => validation);

export const validateAndRedirect = async (data, callback) => {
  const isValidAddress = await validateSchema(data?.customer, addressSchema);
  const isValidUserData = await validateSchema(
    {
      ...data.customer,
      merchant_request_amount: data?.merchant_request_amount,
      accepted_terms: Boolean(data?.terms_accepted_at),
    },
    userDataSchema,
  );

  if (isValidUserData && !isValidAddress) {
    callback(true);
  }
};

export const addressRequiredOnly = yup.object().shape({
  address: yup.string().required(i18n.t("addressForm.addressRequired")),
  postal_code: yup.string().required(i18n.t("addressForm.postalCodeRequired")),
  state: yup.string().required(i18n.t("addressForm.stateRequired")),
  city: yup.string().required(i18n.t("addressForm.cityRequired")),
});

export const userDataRequiredOnly = yup.object().shape({
  document_number: yup.string().required(i18n.t("form.cpfRequired")),
  email: yup.string().required(i18n.t("form.emailRequired")),
  merchant_request_amount: yup
    .number()
    .required(i18n.t("form.amountRequired", { value: 3 })),
  accepted_terms: config.requireTerms
    ? yup.bool().oneOf([true], i18n.t("form.acceptedTermsRequired")).required()
    : yup.bool(),
});
